import Loading from "components/Loading";
import { useUserApi } from "hooks/useApi";
import { useEffect, useState } from "react";
import { Container, Form } from "react-bootstrap";
import "./JurisdictionSelect.scss";
import useJurisdictions from "hooks/useJurisdictions";
import Button from "components/Button";
import { FirmClient } from "services/FirmService";

export default function JurisdictionSelect({ firm, addOffice }: { firm?: FirmClient, addOffice: (firm: FirmClient, jurisdiction: string) => void }) {
  const [useSuggested, setUseSuggested] = useState(true);
  const [otherJurisdiction, setOtherJurisdiction] = useState<string>();
  const [sourceState, setSourceState] = useState<string>();
  const [saving, setSaving] = useState(false);
  const jurisdictions = useJurisdictions();
  const suggestedJx = jurisdictions && sourceState ? jurisdictions.find(x => x.abbreviation === sourceState) ?? jurisdictions.find(x => x.abbreviation === "CA") : undefined;
  const otherJx = (jurisdictions && !!otherJurisdiction) ? jurisdictions.find(x => x.id === otherJurisdiction) : undefined;
  const userApi = useUserApi();

  const selectedIsUnSupported = (!useSuggested && otherJx && !otherJx?.isFullySupported) || (useSuggested && !suggestedJx?.isFullySupported);

  useEffect(() => {
    async function load() {
      setSourceState((await userApi.userLocation()) ?? "CA");
    }
    load();
  }, [userApi]);

  async function submitJurisdiction() {
    setSaving(true);
    addOffice(firm!, useSuggested ? suggestedJx?.id! : otherJx?.id!);
  }

  return (
    <Container className="d-flex justify-content-center align-items-center">
      <div className="jurisdiction-select-container d-flex flex-column justify-content-center align-items-center text-center">
        <Loading isLoading={!suggestedJx}>
          <h2>{`It looks like you're in ${suggestedJx?.friendlyName}. Is that correct?`}</h2>
          <p>Knowing your location allows us to provide a more accurate and tailored experience.</p>
          <div className="jurisdiction-select d-flex flex-column justify-content-left align-items-center">
            <div className="mb-8">
              <Form.Check name="use-suggested" id="suggested-yes" type='radio' checked={useSuggested} label={`Yes, I'm in ${suggestedJx?.friendlyName ?? suggestedJx?.name}`} onChange={() => setUseSuggested(true)} />
              <Form.Check name="use-suggested" id="suggested-no" type='radio' checked={!useSuggested} label="No, I'm in Another State" onChange={() => setUseSuggested(false)} />
            </div>
            {!useSuggested &&
              <div className="mb-8 state-select">
                <label htmlFor="select-jx">Select Your State</label>
                <Form.Select id='select-jx' value={otherJurisdiction ?? ''} onChange={(e) => setOtherJurisdiction(e.target.value)}>
                  <option key="select" value="" disabled>Select</option>
                  {jurisdictions?.sort((a, b) => a.friendlyName!.localeCompare(b.friendlyName!)).filter(x => x.id !== suggestedJx?.id).map(x => {
                    return (
                      <option key={x.id} value={x.id}>{x.friendlyName}</option>
                    );
                  })}
                </Form.Select></div>}
          </div>
          {selectedIsUnSupported &&
            <div className="not-supported justify-content-left">
              <h3>Your Jurisdiction is not yet Fully Supported</h3>
              <p>Unfortunately, at this time Briefpoint only provides limited support to jurisdictions outside of California.</p>
              <p className="highlight">What does this mean?</p>
              <ul>
                <li>Documents you create using Briefpoint may not adhere to your jurisdiction’s formatting rules.</li>
                <li>Our AI hasn’t been trained on your jurisdiction’s documents. Accordingly you may encounter higher rates of inaccuracies and errors. Briefpoint’s Perfect Doc Guarantee will not apply to your documents.</li>
              </ul>
              <p>By continuing you acknowledge this limitation. Contact <a href="mailto:support@briefpoint.ai?subject=Limited Jurisdiction Support">support@briefpoint.ai</a> for more information.</p>
            </div>}
          <div className="d-flex flex-column justify-content-center align-items-center">
            <Button variant="primary" disabled={saving || (!useSuggested && !otherJurisdiction)} onClick={submitJurisdiction} loading={saving} text="Localizing...">{`${selectedIsUnSupported ? 'I Understand' : 'Continue'}`}</Button>
          </div>
        </Loading>
      </div>
    </Container>
  );
}
